import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorResponse, useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import {
  CreateScheduleBatchForm,
  CreateScheduleForm,
  DeleteScheduleForm,
  UpdateScheduleForm,
} from "shared/types";
import { getApiSuccessMessage, getApiErrorMessage } from "shared/utils";
import {
  createScheduleBatchV2,
  createScheduleV2,
  updateScheduleV2,
  deleteScheduleV2,
} from "hooks/api/schedule-v2";
import { invalidateCustomerList } from "hooks/mutations/customerList";

export const useCreateScheduleV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (form: CreateScheduleForm) => createScheduleV2(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-v2"] });
      queryClient.invalidateQueries({ queryKey: ["notification"] });
      getApiSuccessMessage(
        `${translate("schedule.name")} ${translate("messages.success.create")}`,
      );
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// Mirrors useCreateScheduleV2 for the atomic per-day-times batch: same
// invalidations and toasts, one success message for the whole batch.
export const useCreateScheduleBatchV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (form: CreateScheduleBatchForm) => createScheduleBatchV2(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-v2"] });
      queryClient.invalidateQueries({ queryKey: ["notification"] });
      getApiSuccessMessage(
        `${translate("schedule.name")} ${translate("messages.success.create")}`,
      );
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

export const useUpdateScheduleV2 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (form: UpdateScheduleForm) => updateScheduleV2(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-v2"] });
      queryClient.invalidateQueries({ queryKey: ["notification"] });
      getApiSuccessMessage(
        `${translate("schedule.name")} ${translate("messages.success.update")}`,
      );
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

export const useDeleteScheduleV2 = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (form: DeleteScheduleForm) => deleteScheduleV2(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-v2"] });
      queryClient.invalidateQueries({ queryKey: ["waitlist-v2"] });
      queryClient.invalidateQueries({ queryKey: ["booking-v2"] });
      queryClient.invalidateQueries({ queryKey: ["notification"] });
      // Every booking on the deleted class gets its credit back.
      invalidateCustomerList(queryClient);
      getApiSuccessMessage(
        `${translate("schedule.name")} ${translate("messages.success.delete")}`,
      );
      navigate(PATHS.SCHEDULE);
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};
